/**
 * Global components
 * By Juan Rodrigo <jponceflores@svb.com> (www.svb.com)
 * Summary
 * This component renders a SVB checkbox
 */

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * SVBCheckbox
 * @param {string} id - id attribute value for the input tag
 * @param {string} name - name attribute value for the input tag
 * @param {(string|number)} value - value attribute value for the input tag
 * @param {(string|number)} [label = ''] - Label to be displayed next to the checkbox
 * @param {boolean} [defaultChecked = false] - Value that determines if the checkbox is checked by default
 * @param {boolean} [disabled = false] - Value that determines if the checkbox is disabled
 * @param {boolean} [indeterminate = false] - Value that determines if the checkbox is in indeterminate state by default
 * @param {string} [extraClasses = ''] - Extra classes to be added to the button element
 * @param {function} onChange - Function to execute when the checkbox changes (gets clicked)
 * @returns JSX element
 */

export default function SVBCheckbox(props) {
  // Prop error handling
  let componentError = false;

  const typeCheck = (prop, types) => {
    if (!types.includes(typeof prop)) {
      console.log(typeof prop, types, types.includes(typeof prop));
      componentError = true;
    }
    return prop;
  };
  const id = typeCheck(props.id, ['string']);
  const name = typeCheck(props.name, ['string']);
  const value = typeCheck(props.value, ['string', 'number']);
  //const label = typeCheck(props.label, ["string", "number", "undefined"]);
  const label = props.label || '';
  const onChange = props.onChange;
  const defaultChecked = typeCheck(props.defaultChecked, ['boolean', 'undefined']) || false;
  const disabled = typeCheck(props.disabled, ['boolean', 'undefined']) || false;
  const indeterminate = typeCheck(props.indeterminate, ['boolean', 'undefined']) || false;
  const extraClasses = typeCheck(props.extraClasses, ['string', 'undefined']) || '';

  // Prop error check
  if (componentError) return <p>Invalid prop sent, check console for prop type errors.</p>;

  const checkboxRef = useRef(null);

  useEffect(() => {
    if (indeterminate === true) checkboxRef.current.indeterminate = true;
  }, []);

  return (
    <span className={extraClasses}>
      <input
        type='checkbox'
        name={name}
        value={value}
        ref={checkboxRef}
        onChange={onChange}
        className='form-check-input py-0 px-2 me-2'
        id={id}
        defaultChecked={defaultChecked}
        disabled={disabled}
      />
      {label && <label htmlFor={id}>{label}</label>}
    </span>
  );
}

SVBCheckbox.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.any,
  onChange: PropTypes.func,
  defaultChecked: PropTypes.bool,
  disabled: PropTypes.bool,
  indeterminate: PropTypes.bool,
  extraClasses: PropTypes.string,
};
